import { FilterBar } from "@/components/FilterBar";
import { EventHeader } from "@/components/eventHeader";
import { SearchIcon } from "@/components/icons";
import { MainContainer } from "@/components/mainContainer";
import {
  Box,
  Button,
  Divider,
  FormControl,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useOutsideClick,
} from "@chakra-ui/react";
import * as Inputs from "../../../components/inputs";
import * as Fonts from "../../../components/texts";
import * as T from "../../../components/table";
import { useParams } from "react-router-dom";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useCustomToast } from "@/hooks/useToast";
import { Pagination } from "@/components/pagination";
import { useUser } from "@/hooks/useUser";
import {
  EventByIdDto,
  EventEntranceFilterDto,
  UpdateUserForEntranceDto,
  UserForEntranceDto,
} from "../types";
import { useEvent } from "@/hooks/useEvent";
import { useForm } from "react-hook-form";
import LoadingOverlay from "@/components/overlay";
const initialFilter: EventEntranceFilterDto = {
  name: "",
  email: "",
  contact: "",
};

export const EventEntrance = () => {
  const [query, setQuery] = useState("");
  const [isBoxOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<UserForEntranceDto | null>(null);
  const { customToast } = useCustomToast();
  const { id: eventId } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [userToDelete, setUserToDelete] = useState<UserForEntranceDto | null>(
    null
  );
  const [page, setPage] = useState<number>(1);
  const [lastPage] = useState<number>(1);
  const { getEventById, accountForReception, removeAccountForReception } =
    useEvent();
  const { getUserForEntrance, getUserByEventId, updateUserForEntrance } =
    useUser();
  const [users, setUsers] = useState<UserForEntranceDto[]>([]);
  const [entranceUser, setEntranceUser] = useState<UserForEntranceDto[]>([]);
  const [event, setEvent] = useState<EventByIdDto>();
  const [filter, setFilter] = useState<EventEntranceFilterDto>(initialFilter);
  const [typeFilter, setTypeFilter] = useState<string>("");

  const callbackEventReception = useCallback(() => {
    if (!eventId) return;
    setIsLoading(true);
    Promise.all([
      getUserForEntrance(),
      getEventById(Number(eventId)),
      getUserByEventId({ page, eventId: Number(eventId), ...filter }),
    ])
      .then(([userResponse, eventResponse, userByEventResponse]) => {
        setUsers(userResponse.data || []);
        setEvent(eventResponse.data);
        setEntranceUser(userByEventResponse.data.data);
      })
      .catch((error) =>
        customToast("error", [error.response.data.message].join(", "))
      )
      .finally(() => setIsLoading(false));
  }, [page, filter]);

  useEffect(() => {
    callbackEventReception();
  }, [page, filter]);

  const { register, reset, handleSubmit } = useForm<UserForEntranceDto>({});

  const filtered = users.filter((u) =>
    u.name.toLowerCase().includes(query.toLowerCase())
  );

  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick({
    ref,
    handler: () => setIsOpen(false),
  });

  const onSubmit = (data: any) => {
    if (!selected) {
      customToast("error", "Selecione um usuário.");
      setIsLoading(false);
      return;
    }
    const payload: UpdateUserForEntranceDto = {};
    if (data.contact && data.contact !== selected.contact) {
      payload.contact = data.contact;
    }
    setIsLoading(true);
    updateUserForEntrance(selected.id, payload);
    accountForReception(Number(eventId), Number(selected.id))
      .then(() => {
        customToast("success", "Usuário atualizado com sucesso!");
        return getUserByEventId({ eventId: Number(eventId), page, ...filter });
      })
      .catch((error) => {
        customToast("error", error.response?.data?.message);
      })
      .finally(() => {
        callbackEventReception();
        reset();
        onClose();
        setIsLoading(false);
      });
  };

  const handleDeleteUser = () => {
    if (!userToDelete || !eventId) return;
    setIsLoading(true);
    removeAccountForReception(Number(eventId), userToDelete.id)
      .then(() => {
        customToast("success", "Usuário removido da portaria com sucesso!");
        callbackEventReception();
      })
      .catch((error) => {
        customToast("error", error.response?.data?.message);
      })
      .finally(() => {
        setIsLoading(false);
        setUserToDelete(null);
        onDeleteClose();
      });
  };

  const filters = [
    {
      field: (
        <HStack w="30%" justifyContent="flex-end" key={1}>
          <Text
            fontFamily="Inter"
            fontStyle="normal"
            fontWeight="400"
            fontSize="14px"
            lineHeight="17px"
            color="neutral.500"
          >
            Buscar por:
          </Text>
          <Select
            w="70%"
            h={9}
            borderRadius={4}
            bg="neutral.0"
            onChange={(event) => {
              const selectedValue = event.target.value;
              setTypeFilter(selectedValue);
            }}
          >
            <option value="">Selecione</option>
            <option value="name">Nome</option>
            <option value="email">E-mail</option>
            <option value="contact">Telefone</option>
            <option value="profile">Perfil</option>
          </Select>
        </HStack>
      ),
    },
    {
      field: (
        <InputGroup alignItems="center" key={2}>
          <InputLeftElement pointerEvents="none" top="" left={1}>
            <SearchIcon />
          </InputLeftElement>
          <Inputs.SimpleText
            placeholder="O que você procura?"
            w="100%"
            h={9}
            borderRadius={4}
            textIndent="35px"
            bg="neutral.0"
            onChange={(event) => {
              const value = event.target.value;
              setFilter({ ...filter, [typeFilter]: value });
            }}
          />
        </InputGroup>
      ),
    },
    {
      field: (
        <HStack w="30%" justifyContent="flex-start" key={3}>
          <Text
            fontFamily="Inter"
            fontStyle="normal"
            fontWeight="400"
            fontSize="14px"
            lineHeight="17px"
            color="neutral.500"
          >
            Status:
          </Text>
          <Select
            w="70%"
            h={9}
            borderRadius={4}
            bg="neutral.0"
            onChange={(event) => {
              const value = event.target.value;

              let situation: boolean | undefined;

              if (value === "active") {
                situation = true;
              } else if (value === "inactive") {
                situation = false;
              } else {
                situation = undefined;
              }

              setFilter({ ...filter, situation });
            }}
          >
            <option value="">Selecione</option>
            <option value="active">Ativo</option>
            <option value="inactive">Inativo</option>
          </Select>
        </HStack>
      ),
    },
  ];
  return (
    <>
      <EventHeader eventName={event?.name} />
      <MainContainer
        height={isLoading || entranceUser.length < 9 ? undefined : "100%"}
      >
        {isLoading && <LoadingOverlay />}
        <FilterBar
          title={`Usuários Portaria  (${entranceUser.length})`}
          buttons={[
            {
              title: "Vincular um Usuário",
              onClick: onOpen,
            },
          ]}
          filters={filters}
        />
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          isCentered
          closeOnEsc={false}
          closeOnOverlayClick={false}
        >
          <ModalOverlay />
          <ModalContent
            left={142}
            bgColor="neutral.100"
            as="form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <ModalHeader>Vincular Usuário</ModalHeader>
            <ModalBody ref={ref}>
              <Stack spacing={5}>
                <FormControl w="100%">
                  <Fonts.FormLabelText>Evento</Fonts.FormLabelText>
                  <Input
                    type="text"
                    disabled
                    textIndent={"1px"}
                    w="100%"
                    h={10}
                    borderRadius={3}
                    border="1px solid #EAEAEA"
                    bgColor="neutral.0"
                    value={event?.name}
                  />
                </FormControl>
                <FormControl w="100%">
                  <Fonts.FormLabelText>Usuário</Fonts.FormLabelText>
                  <Input
                    bgColor="white"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setIsOpen(true);
                    }}
                    onClick={() => setIsOpen(true)}
                  />
                  {isBoxOpen && filtered.length > 0 && (
                    <List
                      position="absolute"
                      width="100%"
                      bg="white"
                      border="1px solid #ddd"
                      maxHeight="200px"
                      overflowY="auto"
                      mt={1}
                      zIndex={10}
                    >
                      {filtered.map((u) => (
                        <ListItem
                          key={u.id}
                          p={2}
                          _hover={{ bg: "gray.100", cursor: "pointer" }}
                          onClick={() => {
                            setSelected(u);
                            setQuery(u.name);
                            setIsOpen(false);
                            reset(u);
                          }}
                        >
                          <Text>{u.name}</Text>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </FormControl>
                <FormControl w="100%">
                  <Fonts.FormLabelText>E-mail</Fonts.FormLabelText>
                  <Input
                    type="text"
                    textIndent={"1px"}
                    w="100%"
                    h={10}
                    borderRadius={3}
                    border="1px solid #EAEAEA"
                    bgColor="neutral.0"
                    {...register("email")}
                    isDisabled={true}
                  />
                </FormControl>
                <FormControl w="100%">
                  <Fonts.FormLabelText>Telefone</Fonts.FormLabelText>
                  <Input
                    textIndent={"1px"}
                    w="100%"
                    h={10}
                    borderRadius={3}
                    border="1px solid #EAEAEA"
                    bgColor="neutral.0"
                    {...register("contact")}
                  />
                </FormControl>
              </Stack>
            </ModalBody>
            <ModalFooter>
              <HStack w="100%" spacing={7}>
                <Button
                  variant="outline"
                  borderColor="secondary.500"
                  colorScheme="secondary.500"
                  w="100%"
                  h={9}
                  borderRadius={4}
                  onClick={onClose}
                  _hover={{
                    bgColor: "secondary.100",
                  }}
                  _active={{
                    bgColor: "secondary.300",
                  }}
                >
                  <Fonts.ButtonText color="secondary.500">
                    Cancelar
                  </Fonts.ButtonText>
                </Button>
                <Button
                  variant="solid"
                  type="submit"
                  bgColor="secondary.500"
                  w="100%"
                  h={9}
                  borderRadius={4}
                  _hover={{
                    bgColor: "secondary.300",
                  }}
                  _active={{
                    bgColor: "secondary.500",
                  }}
                >
                  <Fonts.ButtonText>Confirmar</Fonts.ButtonText>
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <Modal
          isOpen={isDeleteOpen}
          onClose={onDeleteClose}
          isCentered
          closeOnEsc={false}
          closeOnOverlayClick={false}
        >
          <ModalOverlay />
          <ModalContent left={142} bgColor="neutral.100">
            <ModalHeader>Remover Usuário da Portaria</ModalHeader>
            <ModalBody>
              <Text>
                Tem certeza que deseja remover{" "}
                <strong>{userToDelete?.name}</strong> da portaria deste evento?
              </Text>
            </ModalBody>
            <ModalFooter>
              <HStack w="100%" spacing={7}>
                <Button
                  variant="outline"
                  borderColor="secondary.500"
                  colorScheme="secondary.500"
                  w="100%"
                  h={9}
                  borderRadius={4}
                  onClick={onDeleteClose}
                  _hover={{ bgColor: "secondary.100" }}
                  _active={{ bgColor: "secondary.300" }}
                >
                  <Fonts.ButtonText color="secondary.500">
                    Cancelar
                  </Fonts.ButtonText>
                </Button>
                <Button
                  variant="solid"
                  bgColor="red.500"
                  w="100%"
                  h={9}
                  borderRadius={4}
                  onClick={handleDeleteUser}
                  _hover={{ bgColor: "red.400" }}
                  _active={{ bgColor: "red.600" }}
                >
                  <Fonts.ButtonText>Confirmar</Fonts.ButtonText>
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <Stack p={9} mt={"0 !important"} w="100%">
          <Box bgColor="neutral.100" borderRadius={5} p={3}>
            <HStack w="100%" spacing={5} mt={5}>
              <Stack w="75%">
                <Text
                  fontFamily="Inter"
                  fontStyle="normal"
                  fontWeight="600"
                  fontSize="24px"
                  lineHeight="31px"
                  color="neutral.600"
                >
                  Informações do Evento
                </Text>
              </Stack>
            </HStack>
            <Divider h={2} />
            <Stack p={2}>
              <Stack key={event?.id}>
                <HStack w="100%" spacing={5} mt={5}>
                  <HStack w="40%">
                    <Text fontWeight="semibold">Evento</Text>
                    <Text>{event?.name}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">Tipo do Evento</Text>
                    <Text>{event?.type.name}</Text>
                  </HStack>
                </HStack>
                <HStack w="100%" spacing={5} mt={5}>
                  <HStack w="40%">
                    <Text fontWeight="semibold">Unidade</Text>
                    <Text>{event?.branch.name}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">Data e Hora</Text>
                    <Text>
                      {event?.date ? new Date(event.date).toLocaleString() : ""}
                    </Text>
                  </HStack>
                </HStack>
                <HStack w="100%" spacing={5} mt={5}>
                  <HStack w="40%">
                    <Text fontWeight="semibold">Tipo do Pacote</Text>
                    <Text>{event?.team.name}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">Local</Text>
                    <Text>{event?.local}</Text>
                  </HStack>
                </HStack>
              </Stack>
            </Stack>
          </Box>
          <Stack w="100%">
            <TableContainer w="100%" mt={5}>
              <Table>
                <Thead bgColor="neutral.300">
                  <Tr>
                    <Th w="20%">
                      <Fonts.TableTheadText>Nome</Fonts.TableTheadText>
                    </Th>
                    <Th textAlign="center">
                      <Fonts.TableTheadText>E-mail</Fonts.TableTheadText>
                    </Th>
                    <Th textAlign="center">
                      <Fonts.TableTheadText>Telefone</Fonts.TableTheadText>
                    </Th>
                    <Th textAlign="center">
                      <Fonts.TableTheadText>Perfil</Fonts.TableTheadText>
                    </Th>
                    <Th textAlign="center">
                      <Fonts.TableTheadText>Status</Fonts.TableTheadText>
                    </Th>
                    <Th textAlign="center">
                      <Fonts.TableTheadText>Ações</Fonts.TableTheadText>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {entranceUser.map((user) => (
                    <T.Body key={user.id}>
                      <Td w="20%">
                        <Fonts.TableBodyText>{user.name}</Fonts.TableBodyText>
                      </Td>
                      <Td textAlign="center">
                        <Fonts.TableBodyText>{user.email}</Fonts.TableBodyText>
                      </Td>
                      <Td textAlign="center">
                        <Fonts.TableBodyText>
                          {user.contact}
                        </Fonts.TableBodyText>
                      </Td>
                      <Td textAlign="center">
                        <Fonts.TableBodyText>
                          {user.profile}
                        </Fonts.TableBodyText>
                      </Td>
                      <Td textAlign="center">
                        <Fonts.TableBodyText>
                          {user.situation ? "Inativo" : "Ativo"}
                        </Fonts.TableBodyText>
                      </Td>
                      <Td textAlign="center">
                        <Button
                          size="sm"
                          variant="outline"
                          colorScheme="red"
                          borderRadius={4}
                          onClick={() => {
                            setUserToDelete(user);
                            onDeleteOpen();
                          }}
                        >
                          Remover
                        </Button>
                      </Td>
                    </T.Body>
                  ))}
                </Tbody>
                <Tfoot w="100%" h="150px"></Tfoot>
              </Table>
              <Pagination page={page} lastPage={lastPage} onClick={setPage} />
            </TableContainer>
          </Stack>
        </Stack>
      </MainContainer>
    </>
  );
};
